import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { CheckCircle2, BellRing, Clock, ZapOff, RefreshCw, AlertTriangle, FilePlus2 } from 'lucide-react';
import { puntovService } from '../../../services/api';
import TablaDatos from './TablaDatos';
import FormularioCotizacionLibre from './FormularioCotizacionLibre';
import { useAuth } from '../../../hooks/useAuth';

// La situación la calcula Punto V con sus reglas (fecha límite de 5 días
// hábiles, mora y bloqueo de recarga). Aquí solo se muestra.
const SITUACIONES = {
  pagado: {
    etiqueta: 'Pagado',
    descripcion: 'Último cobro pagado, sin deuda',
    icono: CheckCircle2,
    tono: 'text-green-700 bg-green-50 border-green-200',
    activo: 'ring-green-500'
  },
  notificado: {
    etiqueta: 'Notificado',
    descripcion: 'Recibió el aviso del próximo cobro',
    icono: BellRing,
    tono: 'text-blue-700 bg-blue-50 border-blue-200',
    activo: 'ring-blue-500'
  },
  pendiente: {
    etiqueta: 'Pago pendiente',
    descripcion: 'Cobro emitido, dentro del plazo',
    icono: Clock,
    tono: 'text-amber-700 bg-amber-50 border-amber-200',
    activo: 'ring-amber-500'
  },
  cobro_fallido: {
    etiqueta: 'Cobro fallido',
    descripcion: 'Punto V no pudo generar la mensualidad',
    icono: AlertTriangle,
    tono: 'text-orange-800 bg-orange-50 border-orange-200',
    activo: 'ring-orange-500'
  },
  recarga_cortada: {
    etiqueta: 'Recarga cortada',
    descripcion: 'Venció el plazo de 5 días hábiles: recargas suspendidas',
    icono: ZapOff,
    tono: 'text-red-700 bg-red-50 border-red-200',
    activo: 'ring-red-500'
  }
};

const OTRAS = {
  sin_cobros: { etiqueta: 'Sin cobros', tono: 'text-gray-500 bg-gray-50 border-gray-200' },
  cobro_desactivado: { etiqueta: 'Cobro desactivado', tono: 'text-gray-500 bg-gray-50 border-gray-200' }
};

const infoSituacion = (s) => SITUACIONES[s] || OTRAS[s] || { etiqueta: s, tono: 'text-gray-500 bg-gray-50 border-gray-200' };

const usd = new Intl.NumberFormat('es-VE', { style: 'currency', currency: 'USD' });
const fmtUsd = (v) => (v === null || v === undefined ? '—' : usd.format(Number(v)));

// Fechas puras: se parsea el texto para no correr un día por la zona horaria.
const fmtFecha = (f) => {
  if (!f) return '—';
  const [y, m, d] = String(f).slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};

const fmtPeriodo = (p) => {
  if (!p) return '—';
  const [y, m] = p.split('-');
  const nombre = new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('es-VE', { month: 'long' });
  return `${nombre.charAt(0).toUpperCase()}${nombre.slice(1)} ${y}`;
};

const textoPlazo = (dias) => {
  if (dias === null || dias === undefined) return '';
  if (dias > 1) return `Vence en ${dias} días`;
  if (dias === 1) return 'Vence mañana';
  if (dias === 0) return 'Vence hoy';
  return dias === -1 ? 'Vencido hace 1 día' : `Vencido hace ${-dias} días`;
};

const Insignia = ({ situacion }) => {
  const info = infoSituacion(situacion);
  const Icono = info.icono;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium whitespace-nowrap ${info.tono}`}>
      {Icono && <Icono size={12} />}
      {info.etiqueta}
    </span>
  );
};

const textoBusquedaEmpresa = (e) =>
  `${e.slug} ${e.empresa} ${e.dueno?.nombre || ''} ${e.dueno?.correo || ''} ${e.cobro?.cotizacion_numero || ''} ${e.cobro?.pago?.referencia || ''}`;

const claveEmpresa = (e) => e.empresa;

const columnasBase = [
  {
    id: 'cliente',
    titulo: 'Cliente',
    ordenable: true,
    valor: (e) => e.slug,
    render: (e) => (
      <>
        <div className="font-medium text-gray-800">{e.slug}</div>
        <div className="text-xs text-gray-400">{e.dueno?.nombre || e.empresa}</div>
      </>
    )
  },
  {
    id: 'periodo',
    titulo: 'Periodo',
    ordenable: true,
    valor: (e) => e.cobro?.periodo || '',
    render: (e) => (
      <>
        <div className="text-gray-700">{fmtPeriodo(e.cobro?.periodo)}</div>
        {e.cobro?.cotizacion_numero && (
          <div className="text-xs text-gray-400">Cotización {e.cobro.cotizacion_numero}</div>
        )}
        {e.cobros_adeudados > 1 && (
          <div className="text-xs text-amber-700">y {e.cobros_adeudados - 1} mes(es) más sin pagar</div>
        )}
      </>
    )
  },
  {
    id: 'monto',
    titulo: 'Monto',
    ordenable: true,
    alinear: 'right',
    // Con varios meses sin pagar se ordena por la deuda total, no por el mes mostrado.
    valor: (e) => Number(e.cobros_adeudados > 1 ? e.deuda_total_usd : e.cobro?.total_usd || 0),
    render: (e) => (
      <>
        <div className="font-medium text-gray-800 tabular-nums">{fmtUsd(e.cobro?.total_usd)}</div>
        {Number(e.cobro?.recargo_mora_usd) > 0 && (
          <div className="text-xs text-red-600 tabular-nums">incl. {fmtUsd(e.cobro.recargo_mora_usd)} de mora</div>
        )}
        {e.cobros_adeudados > 1 && (
          <div className="text-xs font-medium text-red-700 tabular-nums">Debe {fmtUsd(e.deuda_total_usd)} en {e.cobros_adeudados} meses</div>
        )}
      </>
    )
  },
  {
    id: 'plazo',
    titulo: 'Fecha límite',
    ordenable: true,
    valor: (e) => e.cobro?.fecha_limite || '',
    render: (e) => {
      if (!e.cobro) return <span className="text-gray-400">—</span>;
      const adeudado = ['pendiente', 'recarga_cortada'].includes(e.situacion);
      return (
        <>
          <div className="text-gray-700 tabular-nums">{fmtFecha(e.cobro.fecha_limite)}</div>
          {adeudado && (
            <div className={`text-xs ${e.cobro.dias_restantes < 0 ? 'text-red-600' : 'text-amber-700'}`}>
              {textoPlazo(e.cobro.dias_restantes)}
            </div>
          )}
        </>
      );
    }
  },
  {
    id: 'detalle',
    titulo: 'Pago / aviso',
    valor: (e) => e.cobro?.pago?.fecha_pago || e.ultimo_aviso || '',
    render: (e) => {
      if (e.fallo_cobro) {
        return (
          <div className="text-xs max-w-xs">
            <div className="font-medium text-orange-800">No se generó {fmtPeriodo(e.fallo_cobro.periodo).toLowerCase()}</div>
            <div className="text-gray-600 break-words">{e.fallo_cobro.error_legible}</div>
            <div className="text-gray-400">{e.fallo_cobro.intentos} intento(s) · revise la pestaña Bases</div>
          </div>
        );
      }
      if (e.cobro?.pago) {
        const p = e.cobro.pago;
        return (
          <>
            <div className="text-gray-700 tabular-nums">Pagó el {fmtFecha(p.fecha_pago)}</div>
            <div className="text-xs text-gray-400">{p.banco} · Ref. {p.referencia}</div>
          </>
        );
      }
      if (e.ultimo_aviso) {
        return <div className="text-gray-600">Avisado el {fmtFecha(e.ultimo_aviso)}</div>;
      }
      return <span className="text-gray-400">—</span>;
    }
  },
  {
    id: 'situacion',
    titulo: 'Situación',
    ordenable: true,
    valor: (e) => infoSituacion(e.situacion).etiqueta,
    render: (e) => <Insignia situacion={e.situacion} />
  }
];

const EstadoPagos = () => {
  const { user } = useAuth();
  const esAdmin = user?.rol === 'admin';
  const [clienteCotizar, setClienteCotizar] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [datos, setDatos] = useState({ hoy: null, empresas: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [situacion, setSituacion] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await puntovService.resumenCobros();
      setDatos(res.data || { hoy: null, empresas: [] });
    } catch (err) {
      setError(err.message || 'Error al consultar el estado de pagos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const resumen = useMemo(() => {
    const r = {};
    Object.keys(SITUACIONES).forEach(k => { r[k] = { cantidad: 0, deuda: 0 }; });
    datos.empresas.forEach(e => {
      if (!r[e.situacion]) return;
      r[e.situacion].cantidad += 1;
      r[e.situacion].deuda += Number(e.deuda_total_usd || 0);
    });
    return r;
  }, [datos.empresas]);

  const columnas = useMemo(() => (esAdmin ? [
    ...columnasBase,
    {
      id: 'acciones',
      titulo: '',
      alinear: 'right',
      valor: () => '',
      render: (e) => (
        <button
          type="button"
          onClick={() => { setAviso(null); setClienteCotizar(e); }}
          title="Cotizar un monto aparte de la mensualidad (integración, instalación…)"
          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 border border-gray-200 rounded-lg hover:border-blue-300 hover:text-blue-700 whitespace-nowrap"
        >
          <FilePlus2 size={13} /> Cotización aparte
        </button>
      )
    }
  ] : columnasBase), [esAdmin]);

  const cerrarCotizar = useCallback(() => setClienteCotizar(null), []);

  const alCrearCotizacion = (r) => {
    setClienteCotizar(null);
    const envio = r.envio;
    setAviso(!envio
      ? { tipo: 'exito', texto: `Cotización ${r.numero} creada sin enviar. La encuentra en la pestaña Cotizaciones.` }
      : envio.enviado
        ? { tipo: 'exito', texto: `Cotización ${r.numero} creada y enviada a ${envio.para}.` }
        : { tipo: 'error', texto: `Cotización ${r.numero} creada, pero el correo no salió: ${envio.error}. Puede reenviarla desde la pestaña Cotizaciones.` });
  };

  const filas = useMemo(
    () => (situacion ? datos.empresas.filter(e => e.situacion === situacion) : datos.empresas),
    [datos.empresas, situacion]
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-gray-500">
          {datos.hoy ? `Situación al ${fmtFecha(datos.hoy)}, según Punto V.` : 'Situación de cobro según Punto V.'}
        </p>
        <button
          onClick={cargar}
          disabled={loading}
          className="px-3 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm disabled:opacity-60"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Actualizar
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {Object.entries(SITUACIONES).map(([clave, info]) => {
          const Icono = info.icono;
          const seleccionada = situacion === clave;
          const { cantidad, deuda } = resumen[clave];
          return (
            <button
              key={clave}
              type="button"
              onClick={() => setSituacion(seleccionada ? null : clave)}
              aria-pressed={seleccionada}
              className={`text-left rounded-xl border p-4 transition-shadow outline-none focus-visible:ring-2 ${info.tono} ${
                seleccionada ? `ring-2 ${info.activo}` : 'hover:shadow-sm'
              }`}
            >
              <div className="flex items-center gap-2 text-sm font-medium">
                <Icono size={16} />
                {info.etiqueta}
              </div>
              <div className="mt-2 text-2xl font-bold tabular-nums">{cantidad}</div>
              <div className="text-xs opacity-80 mt-0.5">
                {deuda > 0 ? `${fmtUsd(deuda)} por cobrar` : info.descripcion}
              </div>
            </button>
          );
        })}
      </div>

      {aviso && (
        <div className={`rounded-xl p-4 text-sm border flex items-start gap-2 ${
          aviso.tipo === 'error' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-green-50 border-green-200 text-green-700'
        }`}>
          {aviso.tipo === 'error' ? <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" /> : <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" />}
          <span>{aviso.texto}</span>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2">
          <h3 className="font-semibold text-gray-800 text-sm">
            {situacion ? SITUACIONES[situacion].etiqueta : 'Todos los clientes con cobro'}
          </h3>
          {situacion && (
            <button onClick={() => setSituacion(null)} className="text-xs text-blue-600 hover:underline">
              Ver todos
            </button>
          )}
        </div>
        {loading && datos.empresas.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : (
          <TablaDatos
            columnas={columnas}
            filas={filas}
            clave={claveEmpresa}
            textoBusqueda={textoBusquedaEmpresa}
            placeholderBusqueda="Buscar cliente, cotización o referencia"
            vacio={situacion ? `Ningún cliente en "${SITUACIONES[situacion].etiqueta}"` : 'Ningún cliente tiene cobro configurado en Punto V'}
          />
        )}
      </div>

      {clienteCotizar && (
        <FormularioCotizacionLibre
          inicial={{ cliente_id: clienteCotizar.erp_cliente_id, empresa: clienteCotizar.empresa, contacto: clienteCotizar.dueno?.nombre }}
          onClose={cerrarCotizar}
          onCreada={alCrearCotizacion}
        />
      )}
    </div>
  );
};

export default EstadoPagos;
